import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { AppStoreRating } from '../trust/AppStoreRating';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Star, Building2, Smartphone, LayoutDashboard, UserRound } from 'lucide-react';

/**
 * Homepage 4-tab positioning section.
 *   CoPilot      = Free guidance & planning tools
 *   App Premium  = Premium intelligence & support
 *   1:1 Support  = Expert human advice
 *   B2B          = Grow your business
 *
 * Copy is outcome-led (confidence / clarity for consumers, opportunities /
 * visibility for businesses). No jargon.
 */

const APP_STORE_URL = 'https://apps.apple.com/au/app/buildpilot/id6477372083';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.buildpilot.android&hl=en_US';

const COPILOT = {
  heading: 'Build with confidence from day one',
  subheading: 'Research, plan and organise before you spend thousands on the wrong call.',
  benefits: [
    'Compare builders, designs & land',
    'Shortlist builders, trades & pros',
    'Suburb & planning guides in seconds',
    'Tools that demystify your build',
    'Independent guidance, same rate everywhere',
    'Live land & house-and-land, daily',
    'Designs matched to your block & budget',
    "Plug into Australia's build network",
  ],
};

const APP_PREMIUM = {
  heading: 'Your home-project advisor in your pocket',
  subheading: 'Premium intelligence, AI help and project support through your whole build.',
  benefits: [
    'Building answers, 24/7',
    'Spot defects before they cost you',
    'Compare quotes before you sign',
    'Priority support throughout',
    'Track decisions, costs & progress',
    'Premium builder & trade matching',
    'Fewer costly variations & surprises',
    'Everything in the free workspace',
  ],
};

const ONE_TO_ONE = {
  heading: 'Independent advice before you make expensive decisions',
  subheading: 'Personal guidance from an experienced advisor, scoped to your project.',
  benefits: [
    'Guidance from concept to completion',
    'Builder selection & tender comparisons',
    'Floor plan, design & budget review',
    'Contract & quote review before signing',
    'Independent advice, no builder ties',
    'Site, phone or video consults',
    'Reduce risk before big decisions',
    'Covered by a separate advisory agreement',
  ],
};

const B2B = {
  heading: 'Grow your business inside the BuildPilot ecosystem',
  subheading: 'Reach homeowners researching, planning and building — all in one platform.',
  benefits: [
    '65,000+ community members',
    '30,000+ email subscribers',
    '50,000+ monthly content views',
    'Exposure across web, app, social & email',
    'Featured profiles & advertising',
    'Monthly reporting & analytics',
    'Reach buyers before competitors do',
    "Organic rankings can't be bought",
  ],
};

const TabButton = ({ active, label, icon: Icon, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={[
      'relative flex-1 inline-flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 sm:py-3.5 px-2.5 sm:px-4 text-[12.5px] sm:text-[15px] font-semibold rounded-full transition-all duration-300 whitespace-nowrap',
      active
        ? 'text-white bg-[#1d1d1f] shadow-lg shadow-black/15'
        : 'text-[#86868b] hover:text-[#1d1d1f]',
    ].join(' ')}
  >
    {Icon && <Icon className="w-4 h-4 shrink-0" strokeWidth={2.2} />}
    <span>{label}</span>
  </button>
);

const BenefitList = ({ items, accent, testId }) => (
  <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-4 sm:gap-x-6 sm:gap-y-3 sm:mb-8" data-testid={testId}>
    {items.map((b, i) => (
      <li key={b} className={`${i >= 6 ? 'hidden sm:flex' : 'flex'} items-start gap-2 sm:gap-2.5`}>
        <span
          className={[
            'w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center shrink-0 mt-[3px] sm:mt-[2px]',
            accent === 'gold' ? 'bg-gradient-to-br from-[#febd59] to-[#fe9d59]' : 'bg-[#1d8efe]/10',
          ].join(' ')}
        >
          <Check
            className={['w-2.5 h-2.5 sm:w-3 sm:h-3', accent === 'gold' ? 'text-white' : 'text-[#1d8efe]'].join(' ')}
            strokeWidth={3}
          />
        </span>
        <span className="text-[13px] sm:text-[15px] text-[#1d1d1f] leading-snug">{b}</span>
      </li>
    ))}
  </ul>
);

const PanelHeader = ({ subheading }) => (
  <div className="mb-3 sm:mb-7 text-center sm:text-left">
    <p className="text-[14px] sm:text-[16px] text-[#5a5a5f] leading-[1.45] sm:leading-[1.55] max-w-[680px] mx-auto sm:mx-0">
      {subheading}
    </p>
  </div>
);

const PanelCard = ({ children, testId }) => (
  <div
    className="bg-gradient-to-br from-white to-[#fafafe] border border-[#e8e8ed] rounded-2xl p-4 sm:p-10 shadow-xl shadow-black/[0.04]"
    data-testid={testId}
  >
    {children}
  </div>
);

/* ── Panels ───────────────────────────────────────────────────────────── */

const CoPilotPanel = () => (
  <PanelCard testId="home-pricing-card-copilot">
    <PanelHeader heading={COPILOT.heading} subheading={COPILOT.subheading} />
    <BenefitList items={COPILOT.benefits} accent="blue" testId="home-pricing-benefits-copilot" />
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
      <Link
        to="/workspace/billing"
        className="inline-flex items-center justify-center gap-2 bg-[#1d1d1f] hover:bg-black text-white px-6 py-3.5 rounded-full font-medium text-[15px] sm:text-[16px] shadow-lg shadow-black/15 transition-all duration-300 hover:-translate-y-0.5"
        data-testid="home-pricing-cta-copilot"
      >
        See BuildPilot Platinum pricing
        <ArrowRight className="w-4 h-4" />
      </Link>
      <Link
        to="/find-a-builder"
        className="inline-flex items-center justify-center gap-2 border border-[#e8e8ed] hover:border-[#1d1d1f] text-[#1d1d1f] px-6 py-3.5 rounded-full font-medium text-[15px] sm:text-[16px] transition-all duration-300 sm:ml-auto"
        data-testid="home-pricing-cta-copilot-find-builder"
      >
        Match me with a builder
      </Link>
    </div>
  </PanelCard>
);

const AppPanel = () => (
  <PanelCard testId="home-pricing-card-app">
    <PanelHeader heading={APP_PREMIUM.heading} subheading={APP_PREMIUM.subheading} />
    <BenefitList items={APP_PREMIUM.benefits} accent="gold" testId="home-pricing-benefits-app" />
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
      <Link
        to="/workspace"
        className="inline-flex items-center justify-center gap-2 bg-[#1d1d1f] hover:bg-black text-white px-6 py-3.5 rounded-full font-medium text-[15px] sm:text-[16px] shadow-lg shadow-black/15 transition-all duration-300 hover:-translate-y-0.5"
        data-testid="home-pricing-cta-app-signup"
      >
        Start Premium today
        <ArrowRight className="w-4 h-4" />
      </Link>
      <div className="flex items-center gap-2 sm:gap-3 sm:ml-auto">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center hover:opacity-85 hover:-translate-y-0.5 transition-all duration-300"
          data-testid="home-pricing-app-store"
        >
          <img
            src="/images/app/app-store-badge.svg"
            alt="Download on the App Store"
            loading="lazy"
            className="h-[42px] sm:h-[46px] w-auto"
          />
        </a>
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center hover:opacity-85 hover:-translate-y-0.5 transition-all duration-300"
          data-testid="home-pricing-google-play"
        >
          <img
            src="/images/app/google-play-badge.png"
            alt="Get it on Google Play"
            loading="lazy"
            className="h-[62px] sm:h-[68px] w-auto"
          />
        </a>
      </div>
    </div>
    <AppStoreRating variant="default" className="mt-4" />
  </PanelCard>
);

const OneToOnePanel = () => (
  <PanelCard testId="home-pricing-card-one-to-one">
    <PanelHeader heading={ONE_TO_ONE.heading} subheading={ONE_TO_ONE.subheading} />
    <BenefitList items={ONE_TO_ONE.benefits} accent="gold" testId="home-pricing-benefits-one-to-one" />
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
      <Link
        to="/contact"
        className="inline-flex items-center justify-center gap-2 bg-[#1d1d1f] hover:bg-black text-white px-6 py-3.5 rounded-full font-medium text-[15px] sm:text-[16px] shadow-lg shadow-black/15 transition-all duration-300 hover:-translate-y-0.5"
        data-testid="home-pricing-cta-one-to-one-book"
      >
        Book a 15-minute intro call
        <ArrowRight className="w-4 h-4" />
      </Link>
      <Link
        to="/legal/one-to-one-support-agreement"
        className="inline-flex items-center justify-center gap-2 border border-[#e8e8ed] hover:border-[#1d1d1f] text-[#1d1d1f] px-6 py-3.5 rounded-full font-medium text-[15px] sm:text-[16px] transition-all duration-300 sm:ml-auto"
        data-testid="home-pricing-cta-one-to-one-terms"
      >
        Read the advisory terms
      </Link>
    </div>
  </PanelCard>
);

const B2BPanel = () => (
  <PanelCard testId="home-pricing-card-b2b">
    <PanelHeader heading={B2B.heading} subheading={B2B.subheading} />
    <BenefitList items={B2B.benefits} accent="blue" testId="home-pricing-benefits-b2b" />
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
      <Link
        to="/business"
        className="inline-flex items-center justify-center gap-2 bg-[#1d1d1f] hover:bg-black text-white px-6 py-3.5 rounded-full font-medium text-[15px] sm:text-[16px] shadow-lg shadow-black/15 transition-all duration-300 hover:-translate-y-0.5"
        data-testid="home-pricing-cta-b2b-list"
      >
        List your business
        <ArrowRight className="w-4 h-4" />
      </Link>
      <Link
        to="/about/how-we-make-money"
        className="inline-flex items-center justify-center gap-2 border border-[#e8e8ed] hover:border-[#1d1d1f] text-[#1d1d1f] px-6 py-3.5 rounded-full font-medium text-[15px] sm:text-[16px] transition-all duration-300 sm:ml-auto"
        data-testid="home-pricing-cta-b2b-how-paid"
      >
        How BuildPilot gets paid
      </Link>
    </div>
  </PanelCard>
);

/* ── Top-level section ────────────────────────────────────────────────── */

const AppPromoSection = () => {
  const [tab, setTab] = useState('copilot');

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BuildPilot',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'iOS, Android, Web',
    offers: [
      { '@type': 'Offer', name: 'BuildPilot Platinum (Web)', priceCurrency: 'AUD' },
      { '@type': 'Offer', name: 'App Premium', priceCurrency: 'AUD' },
    ],
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>
      <section className="py-8 sm:py-16 lg:py-20 bg-white" data-testid="home-pricing-section">
        <div className="max-w-[980px] mx-auto px-5 sm:px-6">
          <div className="text-center mb-4 sm:mb-14">
            <h2 className="text-[22px] sm:text-[34px] lg:text-[40px] font-semibold text-[#1d1d1f] leading-[1.12] tracking-[-0.02em]">
              The <span className="gradient-text-blue">only</span> home-project companion you&apos;ll need
            </h2>
          </div>

          <div
            className="bg-[#f5f5f7] rounded-full p-1.5 mb-5 sm:mb-10 max-w-[680px] mx-auto flex"
            data-testid="home-pricing-tabs"
          >
            <TabButton
              label="Web"
              icon={LayoutDashboard}
              active={tab === 'copilot'}
              onClick={() => setTab('copilot')}
              testId="home-pricing-tab-copilot"
            />
            <TabButton
              label="App"
              icon={Smartphone}
              active={tab === 'app'}
              onClick={() => setTab('app')}
              testId="home-pricing-tab-app"
            />
            <TabButton
              label="1:1"
              icon={UserRound}
              active={tab === 'one-to-one'}
              onClick={() => setTab('one-to-one')}
              testId="home-pricing-tab-one-to-one"
            />
            <TabButton
              label="B2B"
              icon={Building2}
              active={tab === 'b2b'}
              onClick={() => setTab('b2b')}
              testId="home-pricing-tab-b2b"
            />
          </div>

          {tab === 'copilot' && <CoPilotPanel />}
          {tab === 'app' && <AppPanel />}
          {tab === 'one-to-one' && <OneToOnePanel />}
          {tab === 'b2b' && <B2BPanel />}
        </div>
      </section>
    </>
  );
};

export default AppPromoSection;
